import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';

/**
 * Editable fields for workspace creation/editing.
 */
export interface WorkspaceEditableFields {
  name: string;
  caseTypeId: string;
  config?: Record<string, unknown>;
}

export type WorkspaceFormMode = 'new' | 'edit';

export interface WorkspaceFormStateModel {
  organizationId: string | undefined;
  mode: WorkspaceFormMode | undefined;
  draft: WorkspaceEditableFields | undefined;
  originalSnapshot: WorkspaceEditableFields | undefined;
  workspaceId: string | undefined;
  saving: boolean;
  savingError: string | undefined;
}

const EMPTY_DRAFT: WorkspaceEditableFields = { name: '', caseTypeId: '', config: undefined };

@Injectable()
export class WorkspaceFormState extends ComponentStore<WorkspaceFormStateModel> {
  constructor() {
    super({
      organizationId: undefined,
      mode: undefined,
      draft: undefined,
      originalSnapshot: undefined,
      workspaceId: undefined,
      saving: false,
      savingError: undefined,
    });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly organizationId$ = this.select((state) => state.organizationId);
  public readonly mode$ = this.select((state) => state.mode);
  public readonly draft$ = this.select((state) => state.draft);
  public readonly originalSnapshot$ = this.select((state) => state.originalSnapshot);
  public readonly workspaceId$ = this.select((state) => state.workspaceId);
  public readonly saving$ = this.select((state) => state.saving);
  public readonly savingError$ = this.select((state) => state.savingError);

  public readonly isNewWorkspace$ = this.select((state) => state.mode === 'new');

  public readonly isEditingWorkspace$ = this.select(
    (state) => state.mode === 'edit' && state.draft !== undefined
  );

  public readonly isValid$ = this.select((state) => {
    if (!state.draft) return false;
    return state.draft.name.trim().length > 0 && state.draft.caseTypeId.length > 0;
  });

  public readonly hasUnsavedChanges$ = this.select((state) => {
    if (!state.draft || !state.originalSnapshot) return false;
    return (
      state.draft.name !== state.originalSnapshot.name ||
      state.draft.caseTypeId !== state.originalSnapshot.caseTypeId ||
      JSON.stringify(state.draft.config) !== JSON.stringify(state.originalSnapshot.config)
    );
  });

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly initializeForNew = this.updater((state, organizationId: string | undefined) => ({
    ...state,
    organizationId,
    mode: 'new' as const,
    workspaceId: undefined,
    draft: { ...EMPTY_DRAFT },
    originalSnapshot: undefined,
    saving: false,
    savingError: undefined,
  }));

  public readonly initializeForEdit = this.updater(
    (
      state,
      payload: { workspaceId: string; organizationId: string; draft: WorkspaceEditableFields }
    ) => ({
      ...state,
      organizationId: payload.organizationId,
      mode: 'edit' as const,
      workspaceId: payload.workspaceId,
      draft: { ...payload.draft },
      originalSnapshot: { ...payload.draft },
      saving: false,
      savingError: undefined,
    })
  );

  public readonly updateDraft = this.updater((state, partial: Partial<WorkspaceEditableFields>) => {
    if (!state.draft) return state;
    return {
      ...state,
      draft: { ...state.draft, ...partial },
    };
  });

  public readonly setSnapshot = this.updater((state, snapshot: WorkspaceEditableFields) => ({
    ...state,
    originalSnapshot: { ...snapshot },
  }));

  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));

  public readonly setSavingError = this.updater((state, savingError: string | undefined) => ({
    ...state,
    savingError,
    saving: false,
  }));

  public readonly clear = this.updater(() => ({
    organizationId: undefined,
    mode: undefined,
    draft: undefined,
    originalSnapshot: undefined,
    workspaceId: undefined,
    saving: false,
    savingError: undefined,
  }));
}
